/**
 * Namespaced localStorage auth storage.
 *
 * All apps (Customer /app, Admin /admin, Super Admin /super-admin,
 * Rider /rider|/delivery, Seller /seller) are one React SPA on one origin,
 * so a single shared "fd_token" key meant logging into one role's app in
 * one tab silently logged out every other role's tab on the same browser.
 * Storage is now keyed per role so each app section keeps its own session.
 */
const ROLE_PATH_PREFIXES = [
  // Login page is /super-admin/login, but the authenticated app itself is
  // mounted at /super/* (App.js), so both prefixes must map to super_admin
  // or the app pages fall through to the "customer" default and every API
  // call goes out with no Authorization header.
  ["/super-admin", "super_admin"],
  ["/super", "super_admin"],
  ["/admin", "tenant_admin"],
  ["/rider", "delivery_boy"],
  ["/delivery", "delivery_boy"],
  ["/seller", "seller"],
  ["/app", "customer"],
];

export function getRoleForPath(pathname) {
  let path = pathname || "";
  // Native apps' baked-in bootstrap URL is `/t/<slug>/...` (see
  // TenantRedirect.jsx) — for the brief window before TenantRedirect's own
  // effect rewrites it to the canonical `/delivery/login`-style path, the
  // RAW path is still `/t/<slug>/delivery/login`, which matches none of
  // the prefixes below and silently falls through to the "customer"
  // default. Anything that resolves a token/realm during that window
  // (notably AuthProvider's mount-time session check, which only ever
  // runs ONCE) would read the WRONG localStorage key, find no token, and
  // give up permanently for that app session — even though a perfectly
  // valid rider/admin/etc. session exists. This was the actual cause of
  // riders being stuck on the login screen after a full app kill+relaunch
  // despite a valid, persisted session: cold launches hit this exact path
  // shape; a same-page reload (already past the redirect) never did.
  // Stripping the `/t/<slug>` prefix first makes this resolve identically
  // before and after the redirect.
  const tenantPrefixMatch = path.match(/^\/t\/[^/]+(\/.*)?$/);
  if (tenantPrefixMatch) path = tenantPrefixMatch[1] || "";
  for (const [prefix, role] of ROLE_PATH_PREFIXES) {
    if (path.startsWith(prefix)) return role;
  }
  return "customer";
}

// Maps a LITERAL backend role to the client-side storage realm. Most roles
// map 1:1, but "supervisor" shares tenant_admin's realm — both live under
// /admin, one shared session key — since nothing ever reads a dedicated
// "supervisor" bucket. Used by login() below, where the realm must be
// derived from the role that was JUST authenticated, not from whatever page
// happens to be on screen — see that call site for why.
const ROLE_TO_REALM = {
  super_admin: "super_admin",
  tenant_admin: "tenant_admin",
  supervisor: "tenant_admin",
  delivery_boy: "delivery_boy",
  seller: "seller",
  customer: "customer",
};

export function getRealmForRole(role) {
  return ROLE_TO_REALM[role] || "customer";
}

export function getToken(pathname = (typeof window !== "undefined" ? window.location.pathname : "")) {
  try {
    return localStorage.getItem(`fd_token_${getRoleForPath(pathname)}`);
  } catch {
    return null;
  }
}

// Long-lived companion to the access token — exchanged via POST
// /api/auth/refresh for a fresh access token when the current one expires,
// so an expired token no longer forces a full re-login (see lib/api.js's
// response interceptor, which calls this transparently on a 401).
export function getRefreshToken(pathname = (typeof window !== "undefined" ? window.location.pathname : "")) {
  try {
    return localStorage.getItem(`fd_refresh_${getRoleForPath(pathname)}`);
  } catch {
    return null;
  }
}

export function setSession(role, token, userData, refreshToken) {
  localStorage.setItem(`fd_token_${role}`, token);
  localStorage.setItem(`fd_user_${role}`, JSON.stringify(userData));
  if (refreshToken) localStorage.setItem(`fd_refresh_${role}`, refreshToken);
}

// Swaps in a freshly-refreshed access token without touching the stored
// user data or refresh token — used by the silent-refresh flow in lib/api.js.
export function setAccessToken(role, token) {
  localStorage.setItem(`fd_token_${role}`, token);
}

export function clearSession(pathname = (typeof window !== "undefined" ? window.location.pathname : "")) {
  const role = getRoleForPath(pathname);
  localStorage.removeItem(`fd_token_${role}`);
  localStorage.removeItem(`fd_user_${role}`);
  localStorage.removeItem(`fd_refresh_${role}`);
}
